/**
 * RBAC helpers for admin panel feature keys (exact keys from backend catalog).
 */

export const FEATURES = {
  booking_panel: 'booking_panel',
  bookings: 'bookings',
  booking_details: 'booking_details',
  transfer_booking: 'transfer_booking',
  user_assign: 'user_assign',
  room_type_approvals: 'room_type_approvals',
  sf_onboarding: 'sf_onboarding',
  mapping_data: 'mapping_data',
  google_status: 'google_status',
  gha_excel: 'gha_excel',
  gst_status: 'gst_status',
  stayflexi_onboarding: 'stayflexi_onboarding',
  discounts: 'discounts',
  promo_codes: 'promo_codes',
  hotel_accounts: 'hotel_accounts',
  payment_settlements: 'payment_settlements',
  hotel_bank_details: 'hotel_bank_details',
  finance: 'finance',
  settle_invoice: 'settle_invoice',
  compensation_credit_note: 'compensation_credit_note',
  update_linked_account: 'update_linked_account',
  generate_voucher: 'generate_voucher',
  update_sitemap: 'update_sitemap',
  nearby_places: 'nearby_places',
  ai_pricing_discount: 'ai_pricing_discount',
  add_rating: 'add_rating',
  stayflexi_mapping_token: 'stayflexi_mapping_token',
  room_category_manage: 'room_category_manage',
  b2b_admin: 'b2b_admin',
  manage_admin_users: 'manage_admin_users',
  /** Click-to-call on Booking Details + header manual dial */
  ivr_click_to_call: 'ivr_click_to_call',
  /** Superadmin IVR hub (agents, logs, masking, etc.) */
  ivr_admin: 'ivr_admin',
};

/** Route path → required feature key */
export const ROUTE_FEATURE_MAP = {
  '/booking': FEATURES.booking_panel,
  '/bookings': FEATURES.bookings,
  '/prepaid': FEATURES.bookings,
  '/postpaid': FEATURES.bookings,
  '/booking-details': FEATURES.booking_details,
  '/transfer-booking': FEATURES.transfer_booking,
  '/user-assign': FEATURES.user_assign,
  '/room-type-approvals': FEATURES.room_type_approvals,
  '/sf-onboarding': FEATURES.sf_onboarding,
  '/mapping-data': FEATURES.mapping_data,
  '/google-status': FEATURES.google_status,
  '/gha-excel': FEATURES.gha_excel,
  '/gst-status': FEATURES.gst_status,
  '/discounts': FEATURES.discounts,
  '/promo-codes': FEATURES.promo_codes,
  '/hotel-accounts': FEATURES.hotel_accounts,
  '/payment-settlements': FEATURES.payment_settlements,
  '/hotel-bank-details': FEATURES.hotel_bank_details,
  '/finance': FEATURES.finance,
  '/admin-users': FEATURES.manage_admin_users,
  '/ivr': FEATURES.ivr_admin,
};

/**
 * @param {object|null|undefined} user
 * @param {string} key
 */
export const can = (user, key) =>
  !!user?.is_superadmin || (user?.features ?? []).includes(key);

/** IVR click-to-call (booking + header dial) */
export const canIvrC2c = (user) =>
  !!user?.is_superadmin || (user?.features ?? []).includes(FEATURES.ivr_click_to_call);

/** IVR hub at /ivr — superadmin only (never trust features.includes('ivr_admin')) */
export const canIvrHub = (user) => !!user?.is_superadmin;

export const normalizeAdminUser = (raw, fallbackEmail) => {
  if (!raw || typeof raw !== 'object') {
    return {
      id: null,
      name: '',
      email: fallbackEmail || '',
      phone: '',
      type: 'admin',
      is_superadmin: false,
      features: [],
      hotels: [],
      is_active: true,
    };
  }
  const type = raw.type === 'superadmin' ? 'superadmin' : 'admin';
  const isSuper =
    raw.is_superadmin === true ||
    raw.is_superadmin === 1 ||
    raw.is_superadmin === '1' ||
    type === 'superadmin';
  return {
    id: raw.id ?? null,
    name: raw.name || '',
    email: raw.email || fallbackEmail || '',
    phone: raw.phone || '',
    type: isSuper ? 'superadmin' : type,
    is_superadmin: isSuper,
    features: Array.isArray(raw.features) ? raw.features : [],
    hotels: Array.isArray(raw.hotels) ? raw.hotels : [],
    is_active: raw.is_active !== false && raw.is_active !== 0 && raw.is_active !== '0',
  };
};

export const formatForbiddenMessage = (data) => {
  const feature = data?.required_feature;
  if (feature) return `No access (required: ${feature})`;
  return data?.message || 'No access';
};
